import api from './api';

export type ReportType = 'dlp' | 'nac' | 'web-filter' | 'apt' | 'edr';

export interface PdfJobStatus {
  id: string;
  status: 'pending' | 'done' | 'failed';
  errorMessage: string | null;
}

// APT 전용 추가 필터 (대시보드 현재 필터 그대로 캡처)
export interface AptPdfExtra {
  round?: string;
  team?: string;
  tab?: string; // 'dashboard' | 'compare'(데이터 비교 누적)
}

export const pdfJobsService = {
  createJob: async (
    reportType: ReportType,
    period: string,
    companyName: string,
    extra?: AptPdfExtra,
  ): Promise<{ jobId: string }> => {
    const response = await api.post('/pdf/jobs', {
      reportType,
      period,
      companyName,
      ...(extra?.round ? { round: extra.round } : {}),
      ...(extra?.team ? { team: extra.team } : {}),
      ...(extra?.tab ? { tab: extra.tab } : {}),
    });
    return response.data;
  },

  getStatus: async (jobId: string): Promise<PdfJobStatus> => {
    const response = await api.get<PdfJobStatus>(`/pdf/jobs/${jobId}`);
    return response.data;
  },

  downloadPdf: async (jobId: string): Promise<Blob> => {
    const response = await api.get(`/pdf/jobs/${jobId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * jobId 생성 → 2초 간격 폴링 → 완료 시 PDF blob 반환
   * @param signal AbortSignal — unmount 시 폴링 중단용 (실제 잡은 서버에서 계속)
   */
  pollUntilDone: async (
    jobId: string,
    onTick?: (elapsedSec: number) => void,
    signal?: AbortSignal,
  ): Promise<Blob> => {
    const startedAt = Date.now();
    const MAX_WAIT_MS = 5 * 60 * 1000; // 5분

    while (true) {
      if (signal?.aborted) {
        throw new DOMException('PDF 폴링이 취소되었습니다.', 'AbortError');
      }

      const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
      onTick?.(elapsedSec);

      const status = await pdfJobsService.getStatus(jobId);
      if (status.status === 'done') {
        return pdfJobsService.downloadPdf(jobId);
      }
      if (status.status === 'failed') {
        throw new Error(status.errorMessage || 'PDF 생성에 실패했습니다.');
      }
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        throw new Error('PDF 생성 timeout (5분 초과)');
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  },
};
